import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, devtools, createJSONStorage } from 'zustand/middleware';

import {
  type Environment,
  type EnvironmentSummary,
  type RetailerAccessLevel,
  hasSufficientRetailerAccess,
  type RetailerWithEnvironments,
} from '@/types/retailer';

import { Theme } from './preferences';

const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__storage_test__';
    globalThis.localStorage.setItem(testKey, testKey);
    globalThis.localStorage.removeItem(testKey);

    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

const safeLocalStorage: Storage = {
  clear: (): void => {
    if (!storageAvailable) {
      return;
    }

    try {
      globalThis.localStorage.clear();
    } catch {
      // Silently fail
    }
  },
  get length(): number {
    if (!storageAvailable) {
      return 0;
    }

    try {
      return globalThis.localStorage.length;
    } catch {
      return 0;
    }
  },
  removeItem: (name: string): void => {
    if (!storageAvailable) {
      return;
    }

    try {
      globalThis.localStorage.removeItem(name);
    } catch {
      // Silently fail
    }
  },
  key: (index: number): string | null => {
    if (!storageAvailable) {
      return null;
    }

    try {
      return globalThis.localStorage.key(index);
    } catch {
      return null;
    }
  },
  getItem: (name: string): string | null => {
    if (!storageAvailable) {
      return null;
    }

    try {
      return globalThis.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    if (!storageAvailable) {
      return;
    }

    try {
      globalThis.localStorage.setItem(name, value);
    } catch {
      // Silently fail - storage quota exceeded or unavailable
    }
  },
};

const safeStorage = createJSONStorage(() => safeLocalStorage);

export type { RetailerAccessLevel } from '@/types/retailer';
export { hasSufficientRetailerAccess } from '@/types/retailer';
export {
  type AppConfig,
  useConfigStore,
} from './config';
export {
  type HelpPanelState,
  useHelpStore,
} from './help';
export {
  ENVIRONMENT_COLORS,
  ENVIRONMENT_DISPLAY_NAMES,
} from '@/types/retailer';
export type { Environment, EnvironmentSummary, RetailerWithEnvironments } from '@/types/retailer';

// ============ UI Store ============

interface UIState {
  theme: Theme;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setTheme: (theme: Theme) => void;
  titleAddon: import('react').ReactNode;
  setSidebarOpen: (open: boolean) => void;
  setTitleAddon: (node: import('react').ReactNode) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      titleAddon: null,
      sidebarOpen: true,
      theme: Theme.Light,
      setTheme: (theme) =>
        set({
          theme,
        }),
      setTitleAddon: (node) =>
        set({
          titleAddon: node,
        }),
      setSidebarOpen: (open) =>
        set({
          sidebarOpen: open,
        }),
      toggleSidebar: () =>
        set((state) => ({
          sidebarOpen: !state.sidebarOpen,
        })),
    }),
    {
      storage: safeStorage,
      name: 'nexus-admin-ui',
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
      }),
    },
  ),
);

// ============ Notification Store ============

/**
 * Union type for the different notification severity levels.
 */
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

/**
 * Interface representing a notification to be displayed to the user.
 */
export interface Notification {
  id: string;
  title: string;
  message?: string;
  duration?: number;
  type: NotificationType;
}

interface NotificationState {
  notifications: Notification[];
  clearNotifications: () => void;
  removeNotification: (id: string) => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
}

/** Tracks auto-dismiss timers so they can be cancelled on manual removal */
const notificationTimers = new Map<string, ReturnType<typeof setTimeout>>();

const removeNotificationById = (
  id: string,
  set: (fn: (state: NotificationState) => Partial<NotificationState>) => void,
) => {
  notificationTimers.delete(id);
  set((state) => ({
    notifications: state.notifications.filter((n) => n.id !== id),
  }));
};

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  removeNotification: (id) => {
    const timer = notificationTimers.get(id);

    if (timer) {
      clearTimeout(timer);
    }

    removeNotificationById(id, set);
  },
  clearNotifications: () => {
    for (const timer of notificationTimers.values()) {
      clearTimeout(timer);
    }

    notificationTimers.clear();
    set({ notifications: [] });
  },
  addNotification: (notification) => {
    const id = crypto.randomUUID();
    const newNotification = {
      ...notification,
      id,
    };
    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove after duration (default 5 seconds)
    const duration = notification.duration ?? 5000;

    if (duration > 0) {
      const timer = setTimeout(() => removeNotificationById(id, set), duration);
      notificationTimers.set(id, timer);
    }
  },
}));

// ============ Mapping Editor Store ============

/**
 * Interface representing a node in the mapping editor graph visualization.
 */
interface EditorNode {
  id: string;
  field?: string;
  ruleType?: string;
  data: Record<string, unknown>;
  type: 'source' | 'target' | 'rule';
  position: {
    x: number;
    y: number;
  };
}

/**
 * Interface representing a connection edge between nodes in the mapping editor.
 */
interface EditorEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

interface MappingEditorState {
  isDirty: boolean;
  reset: () => void;
  nodes: EditorNode[];
  edges: EditorEdge[];
  sourceTable: string | null;
  targetTable: string | null;
  selectedNodeId: string | null;
  removeNode: (id: string) => void;
  removeEdge: (id: string) => void;
  setDirty: (dirty: boolean) => void;
  addNode: (node: EditorNode) => void;
  addEdge: (edge: EditorEdge) => void;
  setNodes: (nodes: EditorNode[]) => void;
  setEdges: (edges: EditorEdge[]) => void;
  selectNode: (id: string | null) => void;
  setSourceTable: (table: string | null) => void;
  setTargetTable: (table: string | null) => void;
  updateNode: (id: string, updates: Partial<EditorNode>) => void;
}

const initialEditorState = {
  nodes: [],
  edges: [],
  isDirty: false,
  sourceTable: null,
  targetTable: null,
  selectedNodeId: null,
};

export const useMappingEditorStore = create<MappingEditorState>((set) => ({
  ...initialEditorState,
  reset: () => set(initialEditorState),
  setNodes: (nodes) =>
    set({
      nodes,
    }),
  setEdges: (edges) =>
    set({
      edges,
    }),
  setDirty: (dirty) =>
    set({
      isDirty: dirty,
    }),
  selectNode: (id) =>
    set({
      selectedNodeId: id,
    }),
  setSourceTable: (table) =>
    set({
      isDirty: true,
      sourceTable: table,
    }),
  setTargetTable: (table) =>
    set({
      isDirty: true,
      targetTable: table,
    }),
  addNode: (node) =>
    set((state) => ({
      isDirty: true,
      nodes: [...state.nodes, node],
    })),
  addEdge: (edge) =>
    set((state) => ({
      isDirty: true,
      edges: [...state.edges, edge],
    })),
  removeEdge: (id) =>
    set((state) => ({
      isDirty: true,
      edges: state.edges.filter((e) => e.id !== id),
    })),
  updateNode: (id, updates) =>
    set((state) => ({
      isDirty: true,
      nodes: state.nodes.map((n) =>
        n.id === id
          ? {
              ...n,
              ...updates,
            }
          : n,
      ),
    })),
  removeNode: (id) =>
    set((state) => ({
      isDirty: true,
      nodes: state.nodes.filter((n) => n.id !== id),
      edges: state.edges.filter((e) => e.source !== id && e.target !== id),
      selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
    })),
}));

// ============ DLQ Selection Store ============

interface DlqSelectionState {
  clearSelection: () => void;
  selectedMessageIds: string[];
  selectAll: (ids: string[]) => void;
  selectMessage: (id: string) => void;
  toggleMessage: (id: string) => void;
  isSelected: (id: string) => boolean;
  deselectMessage: (id: string) => void;
}

export const useDlqSelectionStore = create<DlqSelectionState>()(
  devtools(
    immer((set, get) => ({
      selectedMessageIds: [],
      isSelected: (id) => get().selectedMessageIds.includes(id),
      clearSelection: () =>
        set({
          selectedMessageIds: [],
        }),
      selectAll: (ids) =>
        set({
          selectedMessageIds: [...ids],
        }),
      selectMessage: (id) =>
        set((state) => {
          if (!state.selectedMessageIds.includes(id)) {
            state.selectedMessageIds.push(id);
          }
        }),
      deselectMessage: (id) =>
        set((state) => {
          const index = state.selectedMessageIds.indexOf(id);

          if (index !== -1) {
            state.selectedMessageIds.splice(index, 1);
          }
        }),
      toggleMessage: (id) =>
        set((state) => {
          const index = state.selectedMessageIds.indexOf(id);

          if (index === -1) {
            state.selectedMessageIds.push(id);
          } else {
            state.selectedMessageIds.splice(index, 1);
          }
        }),
    })),
    {
      name: 'DlqSelection',
    },
  ),
);

// ============ Retailer Type Re-exports & Auth Store ============

export {
  Theme,
  type DateFormat,
  type DlqTimeRange,
  DLQ_TIME_RANGES,
  formatDate,
  formatDateTime,
  usePreferencesStore,
} from './preferences';
export {
  useAuthStore,
  type AuthUser,
  type AuthError,
  type AuthRetailer,
  type AuthUserRole,
  type AuthProvider,
  type LoginRequest,
  type ReauthRequest,
  type LoginResponse,
} from './auth';

// ============ Mapping Viewport Store ============

/**
 * Stored viewport position and zoom for a single mapping canvas.
 */
interface ViewportEntry {
  x: number;
  y: number;
  zoom: number;
}

/**
 * State for persisting mapping canvas viewport positions across sessions.
 */
interface MappingViewportState {
  viewports: Record<string, ViewportEntry>;
  getViewport: (key: string) => ViewportEntry | null;
  saveViewport: (key: string, vp: ViewportEntry) => void;
}

export const useMappingViewportStore = create<MappingViewportState>()(
  persist(
    (set, get) => ({
      viewports: {},
      getViewport: (key) => get().viewports[key] ?? null,
      saveViewport: (key, vp) =>
        set((s) => {
          const entries = Object.entries(s.viewports);
          const trimmed = entries.length >= 100 ? Object.fromEntries(entries.slice(-99)) : s.viewports;

          return { viewports: { ...trimmed, [key]: vp } };
        }),
    }),
    {
      storage: safeStorage,
      name: 'nexus-admin-mapping-viewport',
    },
  ),
);

// ============ Environment Store ============

interface EnvironmentState {
  isLoading: boolean;
  error: string | null;
  clearEnvironment: () => void;
  currentRetailerCode: string | null;
  environments: EnvironmentSummary[];
  hasProductionAccess: () => boolean;
  retailers: RetailerWithEnvironments[];
  currentEnvironment: Environment | null;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  currentEnvSummary: EnvironmentSummary | null;
  setRetailers: (retailers: RetailerWithEnvironments[]) => void;
  setEnvironments: (environments: EnvironmentSummary[]) => void;
  hasEnvironmentAccess: (requiredLevel: RetailerAccessLevel) => boolean;
  getRetailerEnvironments: (retailerCode: string) => EnvironmentSummary[];
  setCurrentEnvironment: (retailerCode: string | null, environment: Environment | null) => void;
}

export const useEnvironmentStore = create<EnvironmentState>()(
  persist(
    devtools(
      (set, get) => ({
        error: null,
        retailers: [],
        isLoading: false,
        environments: [],
        currentEnvSummary: null,
        currentEnvironment: null,
        currentRetailerCode: null,

        setLoading: (loading) =>
          set({
            isLoading: loading,
          }),

        setError: (error) =>
          set({
            error,
            isLoading: false,
          }),

        getRetailerEnvironments: (retailerCode) => {
          const envs = get().environments;

          return envs.filter((e) => e.retailerCode === retailerCode);
        },

        clearEnvironment: () =>
          set({
            error: null,
            currentEnvSummary: null,
            currentEnvironment: null,
            currentRetailerCode: null,
          }),

        hasProductionAccess: () => {
          const summary = get().currentEnvSummary;

          if (!summary) {
            return false;
          }

          return summary.productionAccess;
        },

        hasEnvironmentAccess: (requiredLevel) => {
          const summary = get().currentEnvSummary;

          if (!summary) {
            return false;
          }

          return hasSufficientRetailerAccess(summary.accessLevel, requiredLevel);
        },

        setCurrentEnvironment: (retailerCode, environment) => {
          const envs = get().environments;

          // Try to find the specified environment for the retailer
          let summary =
            retailerCode && environment
              ? (envs.find((e) => e.retailerCode === retailerCode && e.environment === environment) ?? null)
              : null;

          if (retailerCode && !summary) {
            summary = envs.find((e) => e.retailerCode === retailerCode) ?? null;
          }

          set({
            error: null,
            currentEnvSummary: summary,
            currentRetailerCode: retailerCode,
            currentEnvironment: summary?.environment ?? null,
          });
        },

        setEnvironments: (environments) => {
          const currentRetailerCode = get().currentRetailerCode;
          const currentEnvironment = get().currentEnvironment;
          const currentEnvSummary =
            currentRetailerCode && currentEnvironment
              ? (environments.find(
                  (e) => e.retailerCode === currentRetailerCode && e.environment === currentEnvironment,
                ) ?? null)
              : null;

          if (currentRetailerCode && !currentEnvSummary && environments.length > 0) {
            const firstEnv = environments[0];

            if (firstEnv) {
              set({
                environments,
                currentEnvSummary: firstEnv,
                currentEnvironment: firstEnv.environment,
                currentRetailerCode: firstEnv.retailerCode,
              });
            }
          } else {
            set({
              environments,
              currentEnvSummary,
            });
          }
        },

        setRetailers: (retailers) => {
          const allEnvironments: EnvironmentSummary[] = [];

          for (const retailer of retailers) {
            allEnvironments.push(...retailer.environments);
          }

          const persistedRetailerCode = get().currentRetailerCode;
          const persistedEnvironment = get().currentEnvironment;

          const retailerStillAccessible = persistedRetailerCode
            ? retailers.some((r) => r.retailerCode === persistedRetailerCode && r.environments.length > 0)
            : false;

          // Determine effective retailer code (fallback to first available if persisted is inaccessible)
          const effectiveRetailerCode = retailerStillAccessible
            ? persistedRetailerCode
            : (retailers.find((r) => r.environments.length > 0)?.retailerCode ?? null);

          let currentEnvSummary: EnvironmentSummary | null = null;

          if (effectiveRetailerCode && persistedEnvironment && retailerStillAccessible) {
            // Try to find the persisted environment for the effective retailer
            currentEnvSummary =
              allEnvironments.find(
                (e) => e.retailerCode === effectiveRetailerCode && e.environment === persistedEnvironment,
              ) ?? null;
          }

          // Fallback to first available environment for the effective retailer if needed
          if (effectiveRetailerCode && !currentEnvSummary) {
            currentEnvSummary = allEnvironments.find((e) => e.retailerCode === effectiveRetailerCode) ?? null;
          }

          // Final fallback to first available environment from any retailer
          if (!currentEnvSummary && allEnvironments.length > 0) {
            currentEnvSummary = allEnvironments[0] ?? null;
          }

          set({
            retailers,
            currentEnvSummary,
            environments: allEnvironments,
            currentEnvironment: currentEnvSummary?.environment ?? null,
            currentRetailerCode: currentEnvSummary?.retailerCode ?? null,
          });
        },
      }),
      {
        name: 'EnvironmentStore',
      },
    ),
    {
      storage: safeStorage,
      name: 'nexus-admin-environment',
      partialize: (state) => ({
        currentEnvironment: state.currentEnvironment,
        currentRetailerCode: state.currentRetailerCode,
      }),
      onRehydrateStorage: () => () => {
        // Remove after 2026-06-01 — migration cleanup for useRetailerStore removal
        safeLocalStorage.removeItem('nexus-admin-retailer');
      },
    },
  ),
);
